const ENERGY_LOW_THRESHOLD = 0.45
const ENERGY_HIGH_THRESHOLD = 0.55

class Particle {
  constructor({
    color,
    gravity,
    image,
    lifetime,
    rotation,
    rotationRate,
    scale,
    scaleRate,
    shape,
    wobble,
    position,
    velocity,
    emitter,
    trail,
    edgeBehaviour,
    svg,
    drag,
    ctx,
    bufferCanvas,
    bufferContext,
  }) {
    const scaleRender = Range.render(scale)
    this.position = position || new Vector(0, 0)
    this.velocity = velocity || new Vector(0, 0)
    this.gravity = gravity
    this.rotation = Choose.render(Range.render(rotation))
    this.rotationRate = Range.render(rotationRate)
    this.scale = new Vector(scaleRender)
    this.scaleRate = new Vector(Range.render(scaleRate))
    this.drag = Range.render(drag)
    this.lifetime = lifetime
    this.wobble = wobble ? Range.render(wobble) / 10000 : 0
    this.shape = Choose.render(shape)
    this.color = Choose.render(color)
    this.trail = trail
    this.edgeBehaviour = edgeBehaviour
    this.ctx = ctx
    this.canvas = ctx.canvas
    this.firstDraw = Date.now()
    this.lastDraw = Date.now()
    this.timeLived = 0
    this.emitter = emitter
    this.wobbleTimer = -Math.PI + Math.random() * Math.PI * 2
    if (image) {
      const img = new Image()
      img.src = Choose.render(image)
      this.image = img
    }
    this.svg = svg

    if (shape.indexOf('waveform') !== -1) {
      this.path = this.getWaveformPath(shape)
    }

    this.previousPositions = []

    this.bufferCanvas = bufferCanvas
    this.bufferContext = bufferContext

    this.leftEdge = Math.random() * 20
    this.rightEdge = this.canvas.width - Math.random() * 60
    this.topEdge = Math.random() * 20
    this.bottomEdge = this.canvas.height - Math.random() * 20
  }

  move({ delta, energy }) {
    const now = Date.now()
    const dilatedDelta = this.emitter.applyTimeDilation({ delta, energy })
    const impulse = this.emitter.getImpulse(energy)
    const current = {
      position: this.position,
      rotation: this.rotation,
      scale: this.scale,
    }

    if (this.trail.length > 0) {
      this.storeState()
    }

    let acceleration = new Vector(0, this.gravity)
    acceleration = acceleration.add(
      this.emitter.applyGravityPoints({
        position: this.position,
        delta,
        energy,
      }),
    )

    this.velocity = this.velocity.add(acceleration.multiply(dilatedDelta))
    this.velocity = this.velocity.add(
      new Vector(dilatedDelta * this.wobble * Math.sin(this.wobbleTimer), 0),
    )
    this.velocity = this.velocity.add(impulse)
    this.velocity = this.velocity.multiply(
      new Vector(1, 1).add(this.drag.multiply(-0.01 * dilatedDelta)),
    )
    this.velocity = this.handleReflection()
    this.position = this.position.add(this.velocity.multiply(dilatedDelta))
    this.rotation = this.rotation + this.rotationRate * dilatedDelta
    this.scale = this.scale.add(this.scaleRate.multiply(dilatedDelta))
    this.timeLived = this.timeLived + dilatedDelta * 10
    this.lastDraw = now
    this.wobbleTimer += dilatedDelta / 100

    this.edgeStop(current)
  }

  handleReflection() {
    if (this.edgeBehaviour === 'bounce') {
      if (this.position.y > this.canvas.height) {
        const normal = new Vector(0, -1)
        return this.velocity.subtract(
          normal.multiply(2 * this.velocity.dotProduct(normal)),
        )
      } else if (this.position.x > this.canvas.width) {
        const normal = new Vector(1, 0)
        return this.velocity.subtract(
          normal.multiply(2 * this.velocity.dotProduct(normal)),
        )
      } else if (this.position.y < 0) {
        const normal = new Vector(0, 1)
        return this.velocity.subtract(
          normal.multiply(2 * this.velocity.dotProduct(normal)),
        )
      } else if (this.position.x < 0) {
        const normal = new Vector(-1, 0)
        return this.velocity.subtract(
          normal.multiply(2 * this.velocity.dotProduct(normal)),
        )
      }
    }

    return this.velocity
  }

  edgeStop({ position, scale, rotation }) {
    if (this.edgeBehaviour === 'stopBottom') {
      if (this.position.y > this.bottomEdge) {
        this.position = position
        this.scale = scale
        this.rotation = rotation
        this.velocity = new Vector(0, 0)
        this.rotationRate = 0
        this.wobble = 0
      }
    }
  }

  getEdgeAlpha() {
    if (this.edgeBehaviour === 'fade') {
      const alpha = Math.min(
        1,
        (this.bottomEdge - this.position.y) / 25,
        (this.rightEdge - this.position.x) / 25,
        (this.position.y - this.topEdge) / 25,
        (this.position.x - this.leftEdge) / 25,
      )
      return alpha < 0 ? 0 : alpha
    }
    return 1
  }

  addImpulse(impulse) {
    this.impulse = this.impulse.add(impulse)
  }

  preDraw(ctx) {
    ctx.globalAlpha = this.getEdgeAlpha()
    ctx.setTransform(
      this.scale.x,
      0,
      0,
      this.scale.y,
      this.position.x,
      this.position.y,
    )

    ctx.rotate(this.rotation)
  }

  postDraw(ctx) {
    ctx.globalAlpha = 1
    ctx.rotate(0)
    ctx.setTransform(1, 0, 0, 1, 0, 0)
  }

  draw() {
    const ctx = this.ctx

    if (this.trail.length > 0) {
      this.drawTrail(ctx)
    }

    this.preDraw(ctx)

    switch (this.shape) {
      case 'line':
        this.drawLine(ctx)
        break
      case 'square':
        this.drawSquare(ctx)
        break
      case 'roundSquare':
        this.drawRoundSquare(ctx)
        break
      case 'circle':
        this.drawCircle(ctx)
        break
      case 'flame':
        this.drawFlame(ctx)
        break
      case 'image':
        this.drawImage(ctx)
        break
      case 'svg':
        this.drawSvg(ctx)
        break
      case 'waveformExact':
        this.drawPath(ctx)
        break
      case 'waveformClose':
      case 'waveformFast':
      case 'waveformVeryFast':
      case 'waveformLoose':
      case 'waveformVeryLoose':
        this.drawPathRound(ctx)
        break
    }

    this.postDraw(ctx)
  }

  getLifetimeRatio() {
    return this.timeLived / this.lifetime
    return (this.lastDraw - this.firstDraw) / this.lifetime
  }

  drawPath(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.beginPath()

    this.path.forEach(([x, y]) => {
      ctx.lineTo(x, y)
    })
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.closePath()
    ctx.stroke()
    ctx.fill()
  }

  drawPathRound(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.beginPath()

    ctx.moveTo(this.path[0][0], this.path[0][1])
    let i = 0
    for (i = 1; i < this.path.length - 2; i++) {
      const xc = (this.path[i][0] + this.path[i + 1][0]) / 2
      const yc = (this.path[i][1] + this.path[i + 1][1]) / 2
      ctx.quadraticCurveTo(this.path[i][0], this.path[i][1], xc, yc)
    }
    // curve through the last two points
    ctx.quadraticCurveTo(
      this.path[i][0],
      this.path[i][1],
      this.path[i + 1][0],
      this.path[i + 1][1],
    )

    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.closePath()
    ctx.stroke()
    ctx.fill()
  }

  drawSvg(ctx) {
    const color = this.color.render(this.getLifetimeRatio())

    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    const basePath = new Path2D()
    const path = new Path2D(this.svg)
    basePath.addPath(path, {}) //{ a: 1 / 40, d: 1 / 40, e: -12.5, f: -12.5 })
    ctx.fill(path)
  }

  drawLine(ctx) {
    const lineEnd = this.velocity.multiply(4)
    const color = this.color.render(this.getLifetimeRatio())
    ctx.strokeStyle = `rgba(${color})`
    ctx.beginPath() // Start a new path
    ctx.moveTo(-lineEnd.x / 2, -lineEnd.y / 2)
    ctx.lineTo(lineEnd.x / 2, lineEnd.y / 2)
    ctx.lineWidth = 3
    ctx.stroke() // Render the path
    ctx.closePath()
  }

  drawSquare(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.fillRect(-0.5, -0.5, 1, 1)
  }

  drawRoundSquare(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.beginPath()
    ctx.roundRect(-0.5, -0.5, 1, 1, 0.1)
    ctx.fill()
    ctx.closePath()
  }

  drawFlame(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.beginPath()
    const path = new Path2D(
      'M.5176,1.0035A.3782.3782,0,0,0,.734.7219.453.453,0,0,0,.69.4064.8332.8332,0,0,0,.5306.1911,1.4556,1.4556,0,0,0,.2986.0028C.2937,0,.2936-.0006.2932.0011l-.0017.01C.2894.0257.2841.0543.28.0743A.7921.7921,0,0,1,.1662.3568C.15.3812.136.4.1.4469A.5572.5572,0,0,0,.03.552.3045.3045,0,0,0,.0011.656a.3864.3864,0,0,0,0,.0627.3462.3462,0,0,0,.099.1968A.3392.3392,0,0,0,.25,1.01.6552.6552,0,0,0,.5176,1.0035Z',
    )

    ctx.fill(path)
    ctx.closePath()
  }

  drawCircle(ctx) {
    const color = this.color.render(this.getLifetimeRatio())
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    ctx.beginPath()
    ctx.arc(-0.5, -0.5, 1, 0, 2 * Math.PI)
    ctx.fill()
    ctx.closePath()
  }

  drawImage(ctx) {
    const image = this.image
    const color = this.color.getColor(this.getLifetimeRatio())
    const colorNoAlpha = new Color(color.red, color.green, color.blue, 1)
    const c = this.bufferCanvas
    const cctx = this.bufferContext
    const x = -image.width / 2
    const y = -image.height / 2
    if (!image.height || !image.width) {
      return
    }

    if (color.isWhite()) {
      ctx.globalAlpha = ctx.globalAlpha * color.alpha
      return ctx.drawImage(image, x, y)
    }

    c.width = image.width
    c.height = image.height
    cctx.clearRect(0, 0, image.width, image.height)
    cctx.drawImage(image, 0, 0)
    cctx.globalCompositeOperation = 'source-atop'
    cctx.fillStyle = `rgba(${colorNoAlpha.render()}`
    cctx.fillRect(0, 0, image.width, image.height)
    cctx.globalCompositeOperation = 'source-over'

    ctx.globalAlpha = ctx.globalAlpha * color.alpha
    ctx.drawImage(image, x, y)

    ctx.globalCompositeOperation = 'color'

    ctx.drawImage(c, x, y)

    ctx.globalCompositeOperation = 'source-over'
  }

  drawTrail(ctx) {
    const color = this.trail.color.render(this.getLifetimeRatio())
    const edgeAlpha = this.getEdgeAlpha()
    ctx.fillStyle = `rgba(${color})`
    ctx.strokeStyle = `rgba(${color})`
    let step = 1 / (this.previousPositions.length + 1)
    let modifier = 1 - step
    this.previousPositions.forEach(({ position, rotation }) => {
      ctx.globalAlpha = modifier * edgeAlpha
      ctx.setTransform(
        this.scale.x * modifier * this.trail.size,
        0,
        0,
        this.scale.y * modifier * this.trail.size,
        position.x,
        position.y,
      )
      ctx.rotate(this.rotation)
      //ctx.beginPath()
      //ctx.arc(-0.5, -0.5, 1, 0, 2 * Math.PI)
      //ctx.fill()
      //ctx.closePath()
      ctx.fillRect(-0.5, -0.5, 1, 1)
      modifier = modifier - step
    })
  }

  getWaveformPath(shape) {
    const container = window.lastWaveformDraw || { top: [], bottom: [] }
    const points = [...container.top, ...container.bottom]
    const filterAmounts = {
      waveformExact: 1,
      waveformClose: 2,
      waveformFast: 4,
      waveformVeryFast: 8,
      waveformLoose: 16,
      waveformVeryLoose: 32,
    }

    const pointsFiltered = points.filter(
      (any, index) => index % filterAmounts[shape] === 0,
    )

    return pointsFiltered.map(([x, y]) => {
      return [x - this.canvas.width / 2, y - this.canvas.height / 2]
    })
  }

  storeState() {
    this.previousPositions = [
      {
        position: this.position,
        rotation: this.rotation,
      },
      ...this.previousPositions.slice(0, this.trail.length - 1),
    ]
  }
}

class Emitter {
  constructor({
    angle,
    position,
    particle,
    speed,
    rate,
    ctx,
    effects,
    system,
  }) {
    this.angle = angle
    this.position = position
    this.particle = particle
    this.rate = rate
    this.speed = speed
    this.ctx = ctx
    this.timer = rate - 10
    this.effects = effects
    this.bufferCanvas = null
    this.bufferContext = null
    this.system = system
  }

  tick({ delta, energy, max }) {
    const rate = this.rate
    const dilatedDelta = this.applyTimeDilation({ delta, energy })
    const spawnedParticles = []
    if (dilatedDelta > 0 && max > 0) {
      const amountToSpawn =
        rate > 10 ? 1 : Math.max(1, 10 - rate * (1 / dilatedDelta))
      if (this.timer > rate - 10) {
        for (let i = 0; i < amountToSpawn; i++) {
          spawnedParticles.push(this.emit(energy))
        }
        this.timer = 0
      }
      this.timer += dilatedDelta
    }

    return spawnedParticles
  }

  applyTimeDilation({ delta, energy }) {
    let value = delta
    const effects = this.effects.filter((effect) => effect.type === 'time')
    effects.forEach((effect) => {
      const factor = getEffectStrength(effect, 1, energy)
      value = value * factor
    })

    return value
  }

  emit(energy) {
    const width = this.ctx.canvas.clientWidth
    const height = this.ctx.canvas.clientHeight
    const canvasDimensions = new Vector(width, height)
    const angle = Range.render(this.angle)
    let position = Range.render(this.position).multiply(canvasDimensions)

    const spawnOffsetEffects = this.effects.filter(
      (effect) => effect.type === 'spawnOffset',
    )
    spawnOffsetEffects.forEach((effect) => {
      const offset = getEffectStrengthVector(effect, new Vector(0, 0), energy)
      position = position.add(offset.multiply(canvasDimensions))
    })

    const velocity = Vector.fromAngle(angle, Range.render(this.speed))

    let lifetime = Range.render(this.particle.lifetime) * 1000
    const lifetimeEffects = this.effects.filter(
      (effect) => effect.type === 'lifetime',
    )
    lifetimeEffects.forEach((effect) => {
      const strength = getEffectStrength(effect, 1, energy)
      lifetime = lifetime * strength
    })

    return new Particle({
      ...this.particle,
      lifetime,
      velocity,
      position,
      ctx: this.ctx,
      emitter: this,
      bufferCanvas: this.bufferCanvas,
      bufferContext: this.bufferContext,
    })
  }

  getImpulse(energy) {
    const effects = this.effects.filter((effect) => effect.type === 'impulse')
    let impulse = new Vector(0, 0)
    effects.forEach((effect) => {
      const strength = getEffectStrength(effect, 0, energy)
      impulse = impulse.add(Vector.fromAngle(effect.data.direction, strength))
    })
    return impulse
  }

  setBufferCanvas(bufferCanvas, bufferContext) {
    this.bufferContext = bufferContext
    this.bufferCanvas = bufferCanvas
  }

  applyGravityPoints({ position, delta, energy }) {
    let accel = new Vector(0, 0)
    const points = this.effects.filter((effect) =>
      this.filterEffectsByEnergy(effect, 'gravity', energy),
    )
    points.forEach((point) => {
      const { position: pointPosition, radius } = point.data
      const width = this.ctx.canvas.clientWidth
      const height = this.ctx.canvas.clientHeight
      const center = new Vector(
        pointPosition[0] * width,
        pointPosition[1] * height,
      )
      if (pointInCircle(position, center, radius * width)) {
        const strength = getEffectStrength(point, 0, energy)
        const diff = new Vector(center.x - position.x, center.y - position.y)
        const magnitude = diff.getMagnitude()
        const normal = new Vector(diff.x / magnitude, diff.y / magnitude)
        const forceFactor =
          100000 / Math.max(20000, diff.x * diff.x + diff.y * diff.y)
        accel = accel.add(normal.multiply(delta * strength * forceFactor))
      }
    })
    return accel
  }

  filterEffectsByEnergy(effect, type, energy) {
    const energyValid =
      effect.energy === 'all' ||
      (effect.energy === 'below' && energy < ENERGY_LOW_THRESHOLD) ||
      (effect.energy === 'above' && energy > ENERGY_HIGH_THRESHOLD)
    return effect.type === type && energyValid
  }
}

function pointInCircle(point, center, radius) {
  return (
    Math.pow(center.x - point.x, 2) + Math.pow(center.y - point.y, 2) <=
    Math.pow(radius, 2)
  )
}

class ParticleSystem {
  constructor({ background, foreground, emitters, player, waveData }) {
    this.background = background
    this.foreground = foreground
    this.maxParticles = 4000
    this.particles = []
    this.emitters = emitters
    this.energies = []
    this.enabled = false
    this.bgCtx = this.background.getContext('2d')
    this.fgCtx = this.foreground.getContext('2d')
    this.energyIntensity = null
    this.lastDraw = Date.now()
    this.player = player
    this.waveData = waveData

    this.bufferCanvas = document.createElement('canvas')
    this.bufferContext = this.bufferCanvas.getContext('2d')
    this.emitters.forEach((emitter) =>
      emitter.setBufferCanvas(this.bufferCanvas, this.bufferContext),
    )
    this.resetDimensions()
    this.queue()
  }

  resetDimensions() {
    this.background.width = this.background.parentElement.clientWidth
    this.background.height = this.background.parentElement.clientHeight
    this.foreground.width = this.foreground.parentElement.clientWidth
    this.foreground.height = this.foreground.parentElement.clientHeight
    this.width = this.background.width
    this.height = this.background.height
  }

  start() {
    this.enabled = true
  }

  stop() {
    this.enabled = false
    this.clear()
  }

  tick() {
    this.resetDimensions()
    this.clear()
    if (this.enabled) {
      this.update()
      this.draw()
    }
    this.queue()
  }

  clear() {
    this.bgCtx.clearRect(0, 0, this.width, this.height)
    this.fgCtx.clearRect(0, 0, this.width, this.height)
  }

  update() {
    const now = Date.now()
    let delta = (now - this.lastDraw) / 10
    const energy = this.getEnergy()
    this.spawnParticles({ delta, energy })
    this.moveParticles({ delta, energy })
    this.lastDraw = now
  }

  getEnergy() {
    const player = this.player
    const { data, average, min } = this.waveData || {}
    if (data) {
      try {
        const index = Math.ceil(
          (player.getCurrentTime() / player.getDuration()) * data.length,
        )
        const energyData = data[index]
        const energy = (Math.abs(energyData[0]) + Math.abs(energyData[1])) / 2
        if (energy > average) {
          const max = 1
          const progress = (1 - (max - energy) / (max - average)) / 2
          return Math.min(1, 0.5 + progress)
        } else if (energy < average) {
          const progress = (1 - (min - energy) / (min - average)) / 2
          return Math.max(0, 0.5 - progress)
        }
      } catch (e) {}
      return 0.5
    } else if (this.energyIntensity !== null) {
      return this.energyIntensity
    }

    return 0.5
  }

  setEnergyIntensity(value) {
    this.energyIntensity = value
  }

  spawnParticles({ delta, energy }) {
    this.emitters.forEach((emitter) => {
      const max = this.maxParticles - this.particles.length
      const particles = emitter.tick({ delta, energy, max })
      if (particles.length > 0) {
        particles.forEach((particle) => this.particles.push(particle))
      }
    })
  }

  moveParticles({ delta, energy }) {
    const currentParticles = []
    const leftBounds = -300
    const rightBounds = this.width + 100
    const topBounds = -100
    const bottomBounds = this.height + 100
    this.particles.forEach((particle) => {
      const x = particle.position.x
      const y = particle.position.y
      if (
        x > leftBounds &&
        x < rightBounds &&
        y > topBounds &&
        y < bottomBounds &&
        particle.timeLived < particle.lifetime
      ) {
        particle.move({ delta, energy })
        currentParticles.push(particle)
      }
    })
    this.particles = currentParticles
  }

  draw() {
    this.particles.forEach((particle) => particle.draw())
    if (false) {
      this.emitters.forEach((emitter) => {
        const points = emitter.effects.filter(
          (effect) => effect.type === 'gravity',
        )
        points.forEach((point) => {
          const { position, radius } = point.data
          const width = this.fgCtx.canvas.clientWidth
          const height = this.fgCtx.canvas.clientHeight
          this.fgCtx.globalAlpha = 1
          this.fgCtx.rotate(0)
          this.fgCtx.setTransform(1, 0, 0, 1, 0, 0)
          this.fgCtx.beginPath()
          this.fgCtx.arc(
            position[0] * width,
            position[1] * height,
            radius * width,
            0,
            2 * Math.PI,
          )
          this.fgCtx.stroke()
        })
      })
    }
  }

  queue() {
    window.requestAnimationFrame(() => {
      this.tick()
    })
  }

  sum(arr) {
    return arr.reduce((acc, cur) => {
      return acc + cur
    }, 0)
  }
}

function createParticleSystem({
  background,
  foreground,
  data,
  player,
  waveData,
}) {
  const defaultEmitter = {
    position: {
      type: 'vector',
      value: [0, 0],
    },
    speed: {
      type: 'float',
      value: 0,
    },
    angle: {
      type: 'float',
      value: 0,
    },
    rate: {
      type: 'float',
      value: 0,
    },
    layer: {
      type: 'string',
      value: 'foreground',
    },
  }

  const defaultParticle = {
    shape: {
      type: 'string',
      value: 'circle',
    },
    gravity: {
      type: 'float',
      value: 0,
    },
    drag: {
      type: 'vector',
      value: [0, 0],
    },
    bounce: {
      type: 'float',
      value: 0,
    },
    wobble: {
      type: 'float',
      value: 0,
    },
    scale: {
      type: 'float',
      value: 1,
    },
    scaleRate: {
      type: 'float',
      value: 0,
    },
    rotation: {
      type: 'float',
      value: 0,
    },
    rotationRate: {
      type: 'float',
      value: 0,
    },
    lifetime: {
      type: 'float',
      value: 30,
    },
    color: {
      type: 'color',
      value: [255, 255, 255, 1],
    },
    edgeBehaviour: {
      type: 'string',
      value: 'fade',
    },
    image: {
      type: 'string',
      value: '',
    },
    svg: {
      type: 'string',
      value: '',
    },
  }

  const defaultTrail = {
    shape: {
      type: 'string',
      value: 'square',
    },
    length: {
      type: 'float',
      value: 0,
    },
    color: {
      type: 'color',
      value: [255, 255, 255, 1],
    },
    size: {
      type: 'float',
      value: 1,
    },
  }

  const system = {
    waveformData: [],
    prerender: 0,
    emitters: [],
    background: background,
    foreground: foreground,
    player,
    waveData,
  }

  const bgCtx = background.getContext('2d')
  const fgCtx = foreground.getContext('2d')

  data?.emitters?.forEach((emitterData) => {
    const id = emitterData.id
    const emitter = Object.keys(defaultEmitter).reduce((acc, cur) => {
      const emitterConfig = emitterData?.[cur] || defaultEmitter[cur]
      acc[cur] = convertData(emitterConfig)
      return acc
    }, {})

    const particle = Object.keys(defaultParticle).reduce((acc, cur) => {
      const particleConfig =
        emitterData?.particle?.[cur] || defaultParticle[cur]
      acc[cur] = convertData(particleConfig)
      acc.trail = Object.keys(defaultTrail).reduce((trailAcc, trailCur) => {
        const trailConfig =
          emitterData?.particle?.trail?.[trailCur] || defaultTrail[trailCur]
        trailAcc[trailCur] = convertData(trailConfig)
        return trailAcc
      }, {})
      return acc
    }, {})

    const effects = data?.effects.reduce((acc, cur) => {
      if (cur?.emitters?.includes(id)) {
        acc.push(cur)
      }
      return acc
    }, [])

    system.emitters.push(
      new Emitter({
        ...emitter,
        particle,
        background,
        foreground,
        effects,
        ctx: emitter.layer === 'foreground' ? fgCtx : bgCtx,
        system,
      }),
    )
  })

  return new ParticleSystem(system)
}

function convertData(data) {
  let v = data.value
  let type = data.type
  if (type.indexOf('choose') !== -1) {
    return new Choose(data)
  }
  switch (type) {
    case 'float':
      return Number(v)
    case 'string':
      return String(v)
    case 'color':
      return new Color(v[0], v[1], v[2], v[3])
    case 'colorOverLife':
      return new ColorOverLife(v)
    case 'vector':
      return new Vector(v[0], v[1])
    case 'range':
      return new Range(Number(v[0]), Number(v[1]))
    case 'vectorRange':
      return new Range(
        new Vector(v[0][0], v[0][1]),
        new Vector(v[1][0], v[1][1]),
      )
  }
}

class Choose {
  constructor(data) {
    this.data = data
  }

  static render(input) {
    if (input instanceof Choose) {
      const data = input.data
      const tempType = data.type.replace('choose', '')
      const value = data.value[Math.floor(Math.random() * data.value.length)]
      const type = tempType.charAt(0).toLowerCase() + tempType.slice(1)
      return convertData({ type, value })
    }
    return input
  }
}

class Vector {
  constructor(x, y) {
    if (Array.isArray(x)) {
      this.x = x[0]
      this.y = x[1]
    } else if (typeof x === 'object') {
      this.x = x.x
      this.y = x.y
    } else {
      this.x = x || 0
      this.y = y === undefined ? x : y || 0
    }
  }

  get() {
    return { x: this.x, y: this.y }
  }

  add(vector) {
    return new Vector(this.x + vector.x, this.y + vector.y)
  }

  subtract(vector) {
    return new Vector(this.x - vector.x, this.y - vector.y)
  }

  multiply(input) {
    if (input instanceof Vector) {
      return new Vector(this.x * input.x, this.y * input.y)
    }
    return new Vector(this.x * input, this.y * input)
  }

  getMagnitude() {
    return Math.sqrt(this.x * this.x + this.y * this.y)
  }

  getAngle() {
    return Math.atan2(this.y, this.x)
  }

  dotProduct(vector) {
    return this.x * vector.x + this.y * vector.y
  }

  static fromAngle(angle, magnitude) {
    return new Vector(magnitude * Math.cos(angle), magnitude * Math.sin(angle))
  }
}

class Range {
  constructor(start, end) {
    this.start = start
    this.end = end
  }

  static render(input) {
    if (input instanceof Range) {
      if (input.start instanceof Vector) {
        return new Vector(
          input.start.x + Math.random() * (input.end.x - input.start.x),
          input.start.y + Math.random() * (input.end.y - input.start.y),
        )
      }
      return input.start + Math.random() * (input.end - input.start)
    }
    return input
  }
}

class Color {
  constructor(r, g, b, a = 255) {
    this.red = r
    this.green = g
    this.blue = b
    this.alpha = a
  }

  rgba() {
    return `${this.red}, ${this.green}, ${this.blue}, ${this.alpha}`
  }

  render() {
    return this.rgba()
  }

  copy() {
    return new Color(this.red, this.green, this.blue, this.alpha)
  }

  getColor() {
    return this
  }

  isWhite() {
    return this.red === 255 && this.green === 255 && this.blue === 255
  }
}

class ColorOverLife {
  constructor(keyframes) {
    this.keyframes = keyframes
  }

  getColor(lifetimeRatio) {
    const normalizedLife = 100 * lifetimeRatio

    const currentFrameIndex =
      this.keyframes.findLastIndex(
        (frame) => frame.keyframe <= normalizedLife,
      ) || 0

    const currentFrame = this.keyframes[currentFrameIndex]
    const nextFrame =
      this.keyframes[currentFrameIndex + 1] ||
      this.keyframes[this.keyframes.length - 1]

    if (nextFrame.keyframe === currentFrame.keyframe) {
      return new Color(...currentFrame.value)
    }
    const progress =
      (normalizedLife - currentFrame.keyframe) /
      (nextFrame.keyframe - currentFrame.keyframe)

    let red = currentFrame.value[0],
      green = currentFrame.value[1],
      blue = currentFrame.value[2],
      alpha = currentFrame.value[3]

    const nextRed = nextFrame.value[0],
      nextGreen = nextFrame.value[1],
      nextBlue = nextFrame.value[2],
      nextAlpha = nextFrame.value[3]

    if (red !== nextRed) {
      red = red * (1 - progress) + nextRed * progress
    }
    if (green !== nextGreen) {
      green = green * (1 - progress) + nextGreen * progress
    }
    if (blue !== nextBlue) {
      blue = blue * (1 - progress) + nextBlue * progress
    }
    if (alpha !== nextAlpha) {
      alpha = alpha * (1 - progress) + nextAlpha * progress
    }

    return new Color(red, green, blue, alpha)
  }

  render(lifetimeRatio) {
    const color = this.getColor(lifetimeRatio)
    return color.render()
  }
}

function getEffectStrength(effect, baseAmount, energy) {
  const fullAmount = effect.data.amount
  if (effect.energy === 'all') {
    return fullAmount
  } else if (effect.energy === 'above' && energy >= ENERGY_HIGH_THRESHOLD) {
    const eStart = ENERGY_HIGH_THRESHOLD
    const eEnd = 1
    return (
      baseAmount +
      (effect.data.amount - baseAmount) * ((energy - eStart) / (eEnd - eStart))
    )
  } else if (effect.energy === 'below' && energy <= ENERGY_LOW_THRESHOLD) {
    const eStart = ENERGY_LOW_THRESHOLD
    const eEnd = 0
    return (
      baseAmount +
      (effect.data.amount - baseAmount) * ((energy - eStart) / (eEnd - eStart))
    )
  }
  return baseAmount
}

function getEffectStrengthVector(effect, baseAmount, energy) {
  const fullAmount = new Vector(effect.data.amount)
  if (effect.energy === 'all') {
    return fullAmount
  } else if (effect.energy === 'above' && energy >= ENERGY_HIGH_THRESHOLD) {
    const eStart = ENERGY_HIGH_THRESHOLD
    const eEnd = 1
    const modifier = (energy - eStart) / (eEnd - eStart)
    return new Vector(
      baseAmount.x + (fullAmount.x - baseAmount.x) * modifier,
      baseAmount.y + (fullAmount.y - baseAmount.y) * modifier,
    )
  } else if (effect.energy === 'below' && energy <= ENERGY_LOW_THRESHOLD) {
    const eStart = ENERGY_LOW_THRESHOLD
    const eEnd = 0
    const modifier = (energy - eStart) / (eEnd - eStart)
    return new Vector(
      baseAmount.x + (fullAmount.x - baseAmount.x) * modifier,
      baseAmount.y + (fullAmount.y - baseAmount.y) * modifier,
    )
  }
  return baseAmount
}

const RainParticleSystem = {}
const CherryBlossomParticleSystem = {}
export { createParticleSystem, RainParticleSystem, CherryBlossomParticleSystem }
